'use client';

import { createContext, useContext, useEffect, useState } from 'react';

import type { ImpressionContextInterface, ImpressionProviderProps, ImpressionRes } from '../../types/impressions';

const ImpressionContext = createContext<ImpressionContextInterface | undefined>(undefined);

/**
 * Key used to persist an anonymous-user UUID in `localStorage`. Same string as the two pre-extraction
 * implementations in apps/tournament + apps/pickleball, so existing anon ids survive the migration.
 */
const USER_IMPRESSION_KEY = 'user-impression-key';

/** Timeout for client-side engagement requests; matches the upstream Next.js API route's BE-forwarding timeout. */
const IMPRESSION_REQUEST_TIMEOUT_MS = 15000;

/** Generate a UUID via the platform `crypto.randomUUID()`. Falls back to a non-crypto random string for ancient runtimes. */
const generateUuid = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `imp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * PATCH the anon engagement records under `userKey` into the now-logged-in user's record. Private to this
 * module — consumers should never call it directly; the provider invokes it on the anon → logged-in
 * transition. The per-app `/api/v2/impressions/patchActivityOnLogin` route does the actual BE proxy.
 */
const updateImpressions = async (userKey: string): Promise<ImpressionRes> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), IMPRESSION_REQUEST_TIMEOUT_MS);
	try {
		const response = await fetch('/api/v2/impressions/patchActivityOnLogin', {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ user_key: userKey }),
			signal: controller.signal
		});
		return await response.json();
	} finally {
		clearTimeout(timer);
	}
};

/**
 * Provider that maintains a stable `userId` for engagement tracking across an app.
 * - When `session.user.uuid` exists, that becomes the `userId`. Any pre-existing anon UUID in localStorage
 *   is PATCH-merged into the BE record and then cleared.
 * - When the user is anonymous, we reuse a localStorage-persisted UUID (so engagement attribution survives
 *   refreshes) or generate + persist a fresh one.
 *
 * Mount once near the root of the app (typically `app/layout.tsx`).
 */
export const ImpressionProvider = ({ children, session, isLocalhost }: ImpressionProviderProps) => {
	const sessionUUID = session?.user?.uuid ?? undefined;
	// Initialize from session if available; localStorage access is deferred to the effect because it's
	// unavailable during SSR.
	const [userId, setUserId] = useState<string>(sessionUUID || '');

	useEffect(() => {
		if (isLocalhost) return;

		const handleImpressions = async () => {
			const storedUserId = typeof window !== 'undefined' ? localStorage.getItem(USER_IMPRESSION_KEY) : null;

			if (sessionUUID) {
				// Logged-in: if we have a stored anonymous UUID from a prior session, merge its engagement
				// into the now-logged-in user's record, then clear it.
				if (storedUserId) {
					try {
						const response = await updateImpressions(storedUserId);
						if (response.statusCode === 200) {
							localStorage.removeItem(USER_IMPRESSION_KEY);
						}
					} catch (error) {
						console.error('Failed to update impressions:', error);
					}
				}
				setUserId(sessionUUID);
			} else {
				// Anonymous: reuse the stored anon UUID, or generate + persist a fresh one.
				if (storedUserId) {
					setUserId(storedUserId);
				} else {
					const newUserId = generateUuid();
					localStorage.setItem(USER_IMPRESSION_KEY, newUserId);
					setUserId(newUserId);
				}
			}
		};

		handleImpressions();
	}, [sessionUUID, isLocalhost]);

	return <ImpressionContext.Provider value={{ userId, isLocalhost }}>{children}</ImpressionContext.Provider>;
};

/** Read the current engagement `userId` from context. Must be inside an `ImpressionProvider`. */
export const useImpression = () => {
	const context = useContext(ImpressionContext);
	if (!context) {
		throw new Error('useImpression must be used within an ImpressionProvider');
	}
	return context;
};
